import type { ComponentType } from 'react';

/**
 * The assistant is a deterministic command layer: modules register commands, the assistant
 * matches typed or spoken intent to them. An AI planner can later call the same commands as tools.
 */
export interface AssistantContext {
  navigate: (to: string) => void;
  speak: (text: string) => void;
}

export type AssistantReply = { ok: true; message: string; href?: string; open?: 'focus' } | { ok: false; message: string };

export interface AssistantCommand {
  id: string;
  /** Short verb phrase shown in results, e.g. "Add a book". */
  title: string;
  /** Usage shown to people, e.g. "add book <title>". */
  usage: string;
  icon?: ComponentType<{ className?: string }>;
  /** Returns a score (0–1) and the extracted argument, or null when the text is not this command. */
  match: (text: string) => { score: number; arg?: string } | null;
  run: (arg: string | undefined, ctx: AssistantContext) => Promise<AssistantReply>;
  /** Commands that need an argument are not run without one. */
  needsArg?: boolean;
}

const norm = (s: string) => s.trim().replace(/\s+/g, ' ');

/** Matches "<verb> <argument>" for any of the verbs (case-insensitive), e.g. "add book Dune". */
export function verbMatcher(verbs: string[], opts: { argRequired?: boolean } = {}) {
  const sorted = [...verbs].sort((a, b) => b.length - a.length);
  return (text: string) => {
    const t = norm(text).toLowerCase();
    for (const v of sorted) {
      if (t === v) return opts.argRequired ? { score: 0.55 } : { score: 1 };
      if (t.startsWith(`${v} `)) {
        const arg = norm(text).slice(v.length).trim();
        return { score: 0.95, arg };
      }
      // Partial typing: "add bo" suggests "add book".
      if (v.startsWith(t) && t.length >= 2) return { score: 0.4 };
    }
    return null;
  };
}

export function rankCommands(commands: AssistantCommand[], text: string) {
  if (!text.trim()) return [];
  return commands
    .map((c) => ({ c, m: c.match(text) }))
    .filter((x): x is { c: AssistantCommand; m: { score: number; arg?: string } } => !!x.m)
    .sort((a, b) => b.m.score - a.m.score);
}

// ---- Widgets --------------------------------------------------------------------------

export type WidgetSize = 's' | 'm' | 'l' | 'w';

/** A live instrument a module offers for Homebase. Widgets load their own data. */
export interface WidgetDef {
  id: string;
  title: string;
  description: string;
  sizes: WidgetSize[];
  defaultSize: WidgetSize;
  Component: ComponentType<{ size: WidgetSize }>;
}
